import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { LineReveal, FadeUp } from "../components/Reveal";
import {
  ArrowUpRight,
  ArrowDown,
  ArrowRight,
  BrainCircuit,
  Users,
  Compass,
  Rocket,
  GraduationCap,
  BookOpen,
  FileText,
  School,
  Briefcase,
  Heart,
  Check,
} from "lucide-react";
import { posts } from "../data/blog";

const IMG = {
  heroArch: "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY29ycG9yYXRlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NTY5MTcxMXww&ixlib=rb-4.1.0&q=85",
  portrait1: "https://images.pexels.com/photos/30609905/pexels-photo-30609905.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  portrait2: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxjb25maWRlbnQlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODU2OTE3MTF8MA&ixlib=rb-4.1.0&q=85",
  meeting: "https://images.unsplash.com/photo-1559136656-3db4bf6c35f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxjb2xsYWJvcmF0aXZlJTIwbWVldGluZyUyMG1vZGVybiUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3ODU2OTE3MTF8MA&ixlib=rb-4.1.0&q=85",
};

const offers = [
  { code: "S/01", icon: BrainCircuit, title: "Psychometric Career Assessment", body: "Scientifically designed assessments to identify your strengths, interests, and ideal career fit — so your next step is based on evidence, not guesswork." },
  { code: "S/02", icon: Users, title: "One-on-One Counselling", body: "Personalised sessions with an experienced career expert who maps out a realistic path for you — not a generic template." },
  { code: "S/03", icon: Compass, title: "Stream & College Selection", body: "Expert guidance on choosing the right stream, course, and college after 10th, 12th, or graduation — matched to real career outcomes, not just rankings." },
  { code: "S/04", icon: Rocket, title: "Career Transition & Upskilling", body: "For working professionals in non-IT and entry-level IT roles looking to switch careers, move up, or build in-demand skills with confidence." },
];

const steps = [
  { n: "01", t: "Book Your Session", d: "Choose a free discovery call or a full assessment-based session — online, or onsite for institutions." },
  { n: "02", t: "Assess & Understand", d: "Complete a psychometric assessment (where applicable) so we understand your strengths, interests, and goals before we talk." },
  { n: "03", t: "One-on-One Counselling", d: "Sit down with a career expert who has actually worked inside the hiring process — not just studied it — to map your options." },
  { n: "04", t: "Walk Away with a Roadmap", d: "Leave with a clear, practical plan for your education, skills, or next career move." },
];

const reasons = [
  "6+ years inside the real job market — not theory, but hands-on with how hiring actually works.",
  "1,000+ candidates placed at global companies including Amazon, BP, and Microsoft.",
  "Direct industry insight as an Account Manager at an Australia-based recruitment agency.",
  "Onsite career consulting drives run at colleges and institutions in Australia.",
  "Science-backed psychometric assessments — not generic personality quizzes.",
  "Personalised 1-on-1 sessions — not one-size-fits-all reports.",
  "Guidance across every stage: school, college, government exam prep, and career transitions.",
];

const audiences = [
  { icon: GraduationCap, tag: "School Students · Class 9–12", body: "Choose the right stream and subjects — before it becomes a decision made under pressure." },
  { icon: BookOpen, tag: "College & Undergraduate Students", body: "Pick the right major, college, or career direction with real market context — not just popularity." },
  { icon: FileText, tag: "Government Job Aspirants", body: "Clarity on whether to continue your current path or explore other options — an honest, unbiased perspective when you need it most." },
  { icon: School, tag: "Schools & Institutions", body: "Help your students understand in-demand skills and the changing job market, through onsite career consulting drives." },
  { icon: Briefcase, tag: "Working Professionals · Non-IT & Entry-Level IT", body: "Career switches, upskilling, or clarity on your next move — guided by someone who has placed professionals at Amazon, BP, and Microsoft." },
  { icon: Heart, tag: "Parents", body: "Understand how to support your child's career decisions with confidence — instead of guesswork or outdated advice." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const yTitle = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  const featuredPosts = posts.slice(0, 3);

  return (
    <main data-testid="home-page" className="relative">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] pt-[112px] pb-24 overflow-hidden">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 relative">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
                className="flex items-center gap-3 mb-8"
              >
                <span className="rule" />
                <span className="overline text-[#1A1C1A]/70">Est. 2020 — Career Studio</span>
              </motion.div>

              <motion.h1
                style={{ y: yTitle }}
                className="font-serif font-light tracking-[-0.02em] leading-[0.92] text-[13vw] md:text-[10vw] lg:text-[9vw]"
              >
                <LineReveal
                  lines={[
                    <>Your career deserves</>,
                    <>a <em className="italic text-[#C26D5C]">plan</em>,</>,
                    <>not a <span className="italic">guess</span>.</>,
                  ]}
                  delay={0.15}
                  stagger={0.14}
                />
              </motion.h1>
            </div>

            <div className="col-span-12 md:col-span-4 md:pb-6">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.9, ease: [0.33, 1, 0.68, 1] }}
                className="text-base md:text-lg leading-relaxed text-[#1A1C1A]/80 max-w-sm"
              >
                Stop choosing careers based on trends, pressure, or what everyone else is doing. Build a path that actually fits <em className="italic">you</em> — your strengths, your goals, your future.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }}
                className="mt-8 flex flex-wrap items-center gap-4"
              >
                <Link to="/book" data-testid="hero-cta-primary" className="btn-cf">
                  Book a Free Career Call <ArrowUpRight size={14} />
                </Link>
                <Link to="/services" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 text-sm border-b border-[#1A1C1A] pb-1 hover:text-[#C26D5C] hover:border-[#C26D5C] transition-colors">
                  Career Clarity Starts Here <ArrowRight size={14} />
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Parallax band */}
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 1.1, duration: 1.2, ease: [0.33, 1, 0.68, 1] }}
            className="relative mt-16 md:mt-24 h-[52vh] md:h-[64vh] overflow-hidden border border-[#D1CDC7]"
          >
            <motion.img
              src={IMG.heroArch}
              alt="Editorial architecture"
              style={{ y: yImg, scale: scaleImg }}
              className="w-full h-[125%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1C1A]/50 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-[#F4F2F0] gap-4">
              <div className="overline">№ 001 — Trust Signal</div>
              <div className="font-serif italic text-xl md:text-3xl text-right max-w-lg">
                1,000+ placed at Amazon, BP &amp; Microsoft.
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.9 }}
            className="mt-6 flex items-center justify-between text-xs text-[#1A1C1A]/60"
          >
            <div className="flex items-center gap-2 overline">
              <ArrowDown size={14} /> Scroll — The Challenge
            </div>
            <div className="hidden md:flex items-center gap-6">
              <span>Amazon</span><span className="text-[#C26D5C]">✦</span>
              <span>BP</span><span className="text-[#C26D5C]">✦</span>
              <span>Microsoft</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CHALLENGE */}
      <section data-testid="challenge" className="py-28 md:py-40 border-t border-[#D1CDC7]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <FadeUp>
            <div className="flex items-center gap-3 mb-10">
              <span className="rule" /><span className="overline">Chapter I — The Challenge</span>
            </div>
          </FadeUp>

          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <FadeUp className="col-span-12 md:col-span-7">
              <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight">
                You&rsquo;re not confused.<br />
                You&rsquo;re just <em className="italic text-[#C26D5C]">missing a plan</em>.
              </h2>
            </FadeUp>
            <FadeUp className="col-span-12 md:col-span-5 md:pt-6" delay={0.1}>
              <p className="text-base md:text-lg leading-relaxed text-[#1A1C1A]/85">
                Thousands of school students, college students, government exam aspirants, and young professionals lose valuable years chasing careers they picked because of trends, family pressure, friends, or social media — not because it actually suited them.
              </p>
              <p className="mt-6 text-base leading-relaxed text-[#1A1C1A]/80">
                CareerFactors provides genuine, structured career guidance to help you identify your strengths, choose the right direction, and build a practical roadmap for your education, skills, and employment — grounded in what&rsquo;s actually happening in today&rsquo;s job market, not outdated advice.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/book" data-testid="challenge-cta-primary" className="btn-cf">
                  Book a Free Career Call <ArrowUpRight size={14} />
                </Link>
                <Link to="/services" data-testid="challenge-cta-secondary" className="btn-cf-ghost">
                  Explore Career Paths
                </Link>
              </div>

              <p className="mt-8 text-sm leading-relaxed text-[#1A1C1A]/70 max-w-md">
                Whether you&rsquo;re unsure which stream to pick after 10th/12th, a graduate questioning your degree, an aspirant preparing for government services, or a professional considering a switch — the right guidance changes everything.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section aria-hidden className="border-y border-[#D1CDC7] py-8 overflow-hidden">
        <Marquee gradient={false} speed={30} pauseOnHover={false}>
          <span className="font-serif text-6xl md:text-8xl mx-8 editorial-outline">Psychometric</span>
          <span className="font-serif italic text-6xl md:text-8xl mx-8 text-[#1A1C1A]">Counselling</span>
          <span className="font-serif text-6xl md:text-8xl mx-8 editorial-outline">Stream Selection</span>
          <span className="font-serif italic text-6xl md:text-8xl mx-8 text-[#C26D5C]">✦</span>
          <span className="font-serif text-6xl md:text-8xl mx-8 editorial-outline">Career Transition</span>
          <span className="font-serif italic text-6xl md:text-8xl mx-8 text-[#1A1C1A]">Upskilling</span>
          <span className="font-serif text-6xl md:text-8xl mx-8 editorial-outline">Institutions</span>
          <span className="font-serif italic text-6xl md:text-8xl mx-8 text-[#C26D5C]">✦</span>
        </Marquee>
      </section>

      {/* WHAT WE OFFER */}
      <section data-testid="offer" className="py-28 md:py-40">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <FadeUp>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="rule" /><span className="overline">Chapter II — What We Offer</span>
                </div>
                <h2 className="font-serif text-5xl md:text-7xl font-light leading-[0.95] tracking-tight max-w-3xl">
                  Guidance built around <em className="italic text-[#C26D5C]">where you are</em> right now.
                </h2>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <Link to="/services" data-testid="offer-cta" className="btn-cf-ghost">
                See the Full Studio <ArrowUpRight size={14} />
              </Link>
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {offers.map((s, i) => {
              const Icon = s.icon;
              return (
                <FadeUp key={s.code} delay={i * 0.06}>
                  <Link
                    to="/services"
                    data-testid={`offer-card-${s.code.replace('/', '-')}`}
                    className="group block h-full border border-[#1A1C1A] bg-[#F4F2F0] p-8 md:p-10 min-h-[320px] transition-colors duration-500 hover:bg-[#1A1C1A] hover:text-[#F4F2F0]"
                  >
                    <div className="flex items-start justify-between">
                      <span className="overline">{s.code}</span>
                      <ArrowUpRight size={20} className="transition-transform duration-500 group-hover:rotate-45" />
                    </div>
                    <div className="mt-16 flex items-center gap-4 text-[#C26D5C] group-hover:text-[#C26D5C]">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-4 font-serif text-3xl md:text-4xl font-light leading-tight">{s.title}</h3>
                    <p className="mt-4 text-sm md:text-base leading-relaxed opacity-80">{s.body}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
                      Learn More <ArrowRight size={14} />
                    </span>
                  </Link>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works" className="py-28 md:py-40 bg-[#EAE6E1] border-y border-[#D1CDC7]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <FadeUp>
            <div className="flex items-center gap-3 mb-8">
              <span className="rule" /><span className="overline">Chapter III — How It Works</span>
            </div>
            <h2 className="font-serif text-5xl md:text-7xl font-light leading-[0.95] tracking-tight max-w-4xl mb-20">
              Your path to <em className="italic text-[#C26D5C]">clarity</em>, in four simple steps.
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-10">
            {steps.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.08}>
                <div className="border-t border-[#1A1C1A] pt-6 h-full">
                  <div className="font-serif text-6xl md:text-7xl font-light text-[#C26D5C]">{s.n}</div>
                  <h3 className="font-serif text-2xl md:text-3xl font-light mt-4 tracking-tight leading-tight">{s.t}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-[#1A1C1A]/80">{s.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section data-testid="why-choose" className="py-28 md:py-40">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10 items-start">
          <FadeUp className="col-span-12 md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-[#D1CDC7] relative">
              <img src={IMG.portrait2} alt="Founder" className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 overline text-[#F4F2F0] bg-[#1A1C1A]/70 px-2 py-1">Founder — CareerFactors</div>
              <div className="absolute bottom-6 left-6 right-6 text-[#F4F2F0]">
                <div className="overline mb-2">Currently</div>
                <p className="font-serif italic text-2xl md:text-3xl leading-tight">
                  Account Manager, Australia-based recruitment agency.
                </p>
              </div>
            </div>
          </FadeUp>

          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <FadeUp>
              <div className="flex items-center gap-3 mb-6">
                <span className="rule" /><span className="overline">Chapter IV — Why CareerFactors</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.02] tracking-tight">
                Guidance from someone<br /> who has actually been in the <em className="italic text-[#C26D5C]">room</em>.
              </h2>
            </FadeUp>

            <ul className="mt-10 space-y-5">
              {reasons.map((r, i) => (
                <FadeUp key={i} delay={i * 0.05}>
                  <li className="flex items-start gap-4 border-t border-[#D1CDC7] pt-5">
                    <span className="mt-1 w-5 h-5 border border-[#1A1C1A] flex items-center justify-center shrink-0">
                      <Check size={12} strokeWidth={2.2} />
                    </span>
                    <span className="text-base leading-relaxed text-[#1A1C1A]/85">{r}</span>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHO WE HELP */}
      <section data-testid="who-we-help" className="py-28 md:py-40 bg-[#EAE6E1] border-y border-[#D1CDC7]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <FadeUp>
            <div className="flex items-center gap-3 mb-8">
              <span className="rule" /><span className="overline">Chapter V — Who We Help</span>
            </div>
            <h2 className="font-serif text-5xl md:text-7xl font-light leading-[0.95] tracking-tight max-w-4xl mb-16">
              Guidance for every stage of the <em className="italic text-[#C26D5C]">career journey</em>.
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D1CDC7] border border-[#D1CDC7]">
            {audiences.map((a, i) => {
              const Icon = a.icon;
              return (
                <FadeUp key={a.tag} delay={i * 0.05}>
                  <div className="bg-[#EAE6E1] p-8 md:p-10 h-full flex flex-col justify-between min-h-[260px] transition-colors duration-500 hover:bg-[#F4F2F0]">
                    <div>
                      <Icon size={22} strokeWidth={1.5} className="text-[#C26D5C]" />
                      <div className="overline mt-6 text-[#1A1C1A]/70">{a.tag}</div>
                    </div>
                    <p className="mt-6 text-base leading-relaxed text-[#1A1C1A]/85">{a.body}</p>
                  </div>
                </FadeUp>
              );
            })}
          </div>

          <FadeUp delay={0.2}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-[#1A1C1A]/70 italic">
              Available online nationwide, with onsite career consulting sessions for schools, colleges, and institutions.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section data-testid="blog-preview" className="py-28 md:py-40">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <FadeUp>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="rule" /><span className="overline">From the Journal</span>
                </div>
                <h2 className="font-serif text-5xl md:text-6xl font-light leading-[0.95] tracking-tight max-w-3xl">
                  Career insights &amp; <em className="italic text-[#C26D5C]">guidance</em>.
                </h2>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <Link to="/blog" data-testid="blog-preview-cta" className="btn-cf-ghost">
                View All Articles <ArrowUpRight size={14} />
              </Link>
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-14">
            {featuredPosts.map((p, i) => (
              <FadeUp key={p.slug} delay={i * 0.06}>
                <Link to="/blog" data-testid={`blog-preview-${p.slug}`} className="group block">
                  <div className="aspect-[4/5] overflow-hidden border border-[#D1CDC7] mb-6 relative">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                    <div className="absolute top-4 left-4 overline text-[#F4F2F0] bg-[#1A1C1A]/70 px-2 py-1">
                      {p.number} · {p.category}
                    </div>
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl font-light tracking-tight leading-tight group-hover:text-[#C26D5C] transition-colors duration-300">
                    {p.title}
                  </h3>
                  <div className="mt-5 flex items-center gap-6 text-xs uppercase tracking-[0.2em] text-[#1A1C1A]/60">
                    <span>{p.date}</span><span>{p.readTime}</span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta" className="py-28 md:py-40 bg-[#1A1C1A] text-[#F4F2F0]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10 items-center">
          <FadeUp className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-12 h-px bg-[#F4F2F0]/60" />
              <span className="overline text-[#F4F2F0]/70">Chapter VI — Begin</span>
            </div>
            <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light leading-[0.94] tracking-tight">
              Your career shouldn&rsquo;t be a<br />
              <em className="italic text-[#C26D5C]">guessing game</em>.
            </h2>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-[#F4F2F0]/80">
              Get expert guidance today — from someone who has helped over 1,000 professionals land roles at companies like Amazon, BP, and Microsoft.
            </p>
          </FadeUp>

          <FadeUp className="col-span-12 md:col-span-4 md:pt-16" delay={0.1}>
            <Link
              to="/book"
              data-testid="final-cta-btn"
              className="inline-flex items-center gap-3 bg-[#F4F2F0] text-[#1A1C1A] px-8 py-5 uppercase tracking-[0.2em] text-xs font-medium border border-[#F4F2F0] hover:bg-[#C26D5C] hover:border-[#C26D5C] hover:text-[#F4F2F0] transition-colors duration-500"
            >
              Book Your Free Career Call <ArrowUpRight size={14} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
